// FILE: frontend/src/pages/Discover.js  (NEW FILE)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Compass, Loader2, Search, Users } from 'lucide-react';
import TasteMatchCard from '@/components/TasteMatchCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function SkeletonCard() {
  return (
    <div className="bg-card border border-white/6 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full skeleton-shimmer bg-muted/30" />
        <div className="space-y-2 flex-1">
          <div className="h-4 w-28 rounded skeleton-shimmer bg-muted/30" />
          <div className="h-3 w-20 rounded skeleton-shimmer bg-muted/30" />
        </div>
      </div>
      <div className="h-3 w-3/4 rounded skeleton-shimmer bg-muted/30" />
      <div className="flex gap-1.5">
        {[1,2,3,4].map(i => (
          <div key={i} className="w-10 h-[60px] rounded-lg skeleton-shimmer bg-muted/30" />
        ))}
      </div>
      <div className="h-9 rounded-xl skeleton-shimmer bg-muted/30" />
    </div>
  );
}

export default function Discover() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(false);
  const [mode, setMode]           = useState('taste_match');
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout             = React.useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    fetchUsers(1, true);
  }, []);

  const fetchUsers = async (pageNum = 1, replace = true) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/discover/people?page=${pageNum}&limit=12`,
        { withCredentials: true }
      );
      const data = res.data;
      setUsers(prev => replace ? (data.users || []) : [...prev, ...(data.users || [])]);
      setHasMore(data.has_more || false);
      setMode(data.mode || 'popular');
      setPage(pageNum);
    } catch { /* ignore */ }
    finally { setLoading(false); setLoadingMore(false); }
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/users/search?q=${encodeURIComponent(q)}`,
          { withCredentials: true }
        );
        setSearchResults(res.data || []);
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
  };

  const displayUsers = searchQuery ? searchResults : users;

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page header */}
        <div className="mb-10 text-center max-w-xl mx-auto">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/15 border border-primary/20 mb-5">
            <Compass className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-3">Discover People</h1>
          <p className="text-muted-foreground text-lg">
            {mode === 'taste_match'
              ? 'People who share your exact movie taste — ranked by how much you have in common.'
              : 'Popular members of the community.'}
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md mx-auto mb-10">
          <div className="flex items-center gap-2 bg-card border border-white/10 rounded-full px-4 py-3 focus-within:border-primary/50 transition-colors">
            {searching
              ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />
              : <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            }
            <input
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by name…"
              className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Mode indicator */}
        {!searchQuery && (
          <div className="flex items-center gap-2 mb-6">
            {mode === 'taste_match' ? (
              <span className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full">
                Ranked by taste overlap
              </span>
            ) : (
              <span className="text-xs font-bold text-muted-foreground bg-white/5 border border-white/10 px-3 py-1 rounded-full">
                Popular members
              </span>
            )}
            <span className="text-xs text-muted-foreground">{users.length} people found</span>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : displayUsers.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-white/8 rounded-2xl">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <p className="text-gray-500 text-lg">
              {searchQuery ? `No users found for "${searchQuery}"` : 'No recommendations yet — build your lists to get matched!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
            {displayUsers.map(user => (
              <TasteMatchCard
                key={user.user_id}
                user={user}
                currentUser={currentUser}
                onFollowChange={() => fetchUsers(1, true)}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && !loading && !searchQuery && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => fetchUsers(page + 1, false)}
              disabled={loadingMore}
              className="flex items-center gap-2 px-7 py-3 bg-white/5 border border-white/10 rounded-full text-sm font-semibold text-gray-300 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50"
            >
              {loadingMore
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                : 'Show more'
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
